import { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import AppConstants from '../AppConstants';
// import Checkins from '../Checkins'; // TODO: Implement Checkins functionality
import CloudBackground from '../elements/CloudBackground';
import ContentItemsList from '../elements/ContentItemsList';
// import LocationHuntScreen from './LocationHunt'; // TODO: Import when LocationHunt component is available
import { useAPI } from '../contexts/APIContext';
import { useJourney } from '../contexts/JourneyContext';
import FadeIn from '../elements/FadeIn';
import GraphicBackground from '../elements/GraphicBackground';
import SmallButton from '../elements/SmallButton';
import Toolbar from '../elements/Toolbar';
import styles from '../Styles';
import ScreenLocationHunt from './ScreenLocationHunt';
// import ToolbarButton from '../elements/ToolbarButton'; // TODO: Import when ToolbarButton component is available

const ScreenPrelude = () => {
  const journeyContext = useJourney();
  const api = useAPI();
  
  // State management
  const [isShowingIntro, setIsShowingIntro] = useState(false);
  const [isShowingDemoHunt, setIsShowingDemoHunt] = useState(false);
  const [isShowingLocationHuntButton, setIsShowingLocationHuntButton] = useState(true);
  const [dateMessage, setDateMessage] = useState(null);
  
  // Countdown functionality (previously Countdown component)
  const calculateDateMessage = useCallback(() => {
    if (!journeyContext.serverData.start_at) return;
    
    let result = null;
    let startDateTime = new Date(journeyContext.serverData.start_at);
    let preludeHuntStartTime = new Date(journeyContext.serverData.prelude_hunt_start_at);
    let currentTime = new Date();
    let timeToLive = startDateTime - currentTime;
    let minuteLength = 1000 * 60;
    let hourLength = minuteLength * 60;
    let dayLength = hourLength * 24;

    if (timeToLive > dayLength) {
      result = null;
    } else if (timeToLive > hourLength) {
      let hoursNum = Math.floor(timeToLive / hourLength);
      if (hoursNum === 1) {
        result = "Your performance will begin in an hour."
      } else {
        result = "Your performance will begin in " + hoursNum + " hours.";
      }
    } else if (timeToLive > minuteLength) {
      let minutesNum = Math.floor(timeToLive / minuteLength);
      if (minutesNum === 1) {
        result = "Your performance will begin very soon."
      } else {
        result = "Your performance will begin in " + minutesNum + " minutes.";
      }
    } else {
      let secondsNum = Math.floor(timeToLive / 1000);
      if (secondsNum < 2) {
        result = "Let's Start";
      } else {
        result = "" + secondsNum + " seconds";
      }
    }
    setDateMessage(result);

    if (preludeHuntStartTime && currentTime >= preludeHuntStartTime) {
      setIsShowingLocationHuntButton(true);
    } else {
      setIsShowingLocationHuntButton(false);
    }
  }, [journeyContext.serverData.start_at, journeyContext.serverData.prelude_hunt_start_at]);

  // Timer effect for countdown updates - sync with JourneyContext timer
  useEffect(() => {
    calculateDateMessage();
  }, [journeyContext.timerTick, calculateDateMessage]);

  // Render countdown display
  const renderCountdown = () => {
    //console.log("Rendering countdown with dateMessage:", dateMessage);
    if (dateMessage !== null) {
      return (
        <Text style={styles.preludeText}>{dateMessage}</Text>
      );
    } else if (journeyContext.serverData.start_at) {
      let startDateTime = new Date(journeyContext.serverData.start_at);
      let dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      let formattedDate = startDateTime.toLocaleDateString(undefined, dateOptions);
      let timeOptions = { hour: 'numeric', minute: '2-digit'}
      let formattedTime = startDateTime.toLocaleTimeString(undefined, timeOptions);

      return (
        <Text style={localStyles.preludeText}>
          Your journey will begin on
          {"\n"}
          {formattedDate}
          {"\n"}
          at {formattedTime}
        </Text>
      );
    } else {
      return null;
    }
  };

  const startIntro = () => {
    setIsShowingIntro(true);
    api.startedPrelude(journeyContext.voyagerCode);
  };

  const startedIntro = () => {
  };
  const finishedIntro = () => {
    setIsShowingIntro(false);
  };

  const startTestRun = () => {
    setIsShowingDemoHunt(true);
  };

  const finishedTestRun = () => {
    setIsShowingDemoHunt(false);
  }

  const localStyles = StyleSheet.create({
    preludeFrame: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#eee',
      opacity: 0.85,
      paddingTop: 30,
      margin: 25,
      marginTop: 100,
      padding: 20,
      borderRadius: 20,
    },

    preludeText: {
      paddingTop: 40,
      paddingLeft: 20,
      paddingRight: 20,
      paddingBottom: 20,
      color: '#666',
      textAlign: 'center',
      fontSize: 16,
    },
  });

  if (isShowingDemoHunt === true && journeyContext.preludeLocation()) {   
    return (
      <ScreenLocationHunt 
        location={journeyContext.preludeLocation() }
        onComplete={finishedTestRun}
        />
    )
  } else if (isShowingIntro === true) {
      return (
        <GraphicBackground source={journeyContext.serverData.background_for_prelude} >
          {/* Ensure the ScrollView fills available space and doesn't cause the background to grow as items are added. */}
          <ScrollView style={{paddingTop: 60, flex: 1}} contentContainerStyle={{flexGrow: 1}}>
            <ContentItemsList
              contentItems={journeyContext.preludeItems()}
              completionButtonTheme="dark"
              completionButtonLabel="Done"
              startedContent={() => startedIntro()}
              completionButtonTapped={() => finishedIntro()}
            />
          </ScrollView>
          <Toolbar showClose={true} onClose={() => finishedIntro()}/>
        </GraphicBackground>
      );
  } else {
    return (
      <CloudBackground >
        <FadeIn>
          <View style={{marginTop: 100, marginBottom: 0, alignItems: 'center'}}>
            <Text style={styles.headingText}>HereNowThis</Text>
          </View>
          <View style={localStyles.preludeFrame}>
            <View style={{padding: 20}}>{renderCountdown()}</View>
            <SmallButton title="Read the Introduction"
              onPress={() => startIntro()}
            />
            {isShowingLocationHuntButton && (
              <View style={{marginTop: 20}}>
                <SmallButton title="Start the Practice Journey"
                  onPress={() => startTestRun()}
                />
              </View>
            )}
            {journeyContext.preludeLocation && !(isShowingLocationHuntButton) && (
              <Text style={{textAlign: 'center', marginTop: 30, fontSize: 12}}>
                The launch will start at {new Date(journeyContext.serverData.prelude_hunt_start_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </Text>
            )}
            <Text style={{textAlign: 'center', paddingTop: 5}}>{AppConstants.version}</Text>
          </View>
        </FadeIn>

        <Toolbar />
      </CloudBackground>
    );
  }
};

export default ScreenPrelude;
